import React from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Chip,
    Box,
    Tooltip,
    IconButton,
} from '@mui/material';
import {
    School as CourseIcon,
    Article as LessonIcon,
    ContentCopy as CopyIcon,
    Link as LinkIcon,
    Business as OperatorIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Share as ShareIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { LibraryItem } from '../../services/libraryService';

interface LibraryItemCardProps {
    item: LibraryItem;
    onClick?: (item: LibraryItem) => void;
    onInsert?: (item: LibraryItem) => void;
    onEdit?: (item: LibraryItem) => void;
    onDelete?: (item: LibraryItem) => void;
    onShare?: (item: LibraryItem) => void;
    isOperator?: boolean;
    showActions?: boolean;
}

const LibraryItemCard: React.FC<LibraryItemCardProps> = ({
    item,
    onClick,
    onInsert,
    onEdit,
    onDelete,
    onShare,
    isOperator = false,
    showActions = true,
}) => {
    const translate = useTranslate();

    const getTypeIcon = () => {
        return item.itemType === 'course' ? (
            <CourseIcon fontSize="small" color="primary" />
        ) : (
            <LessonIcon fontSize="small" color="secondary" />
        );
    };

    const getTypeLabel = () => {
        return item.itemType === 'course'
            ? translate('library.types.course')
            : translate('library.types.lesson');
    };

    const getAccessChip = () => {
        if (item.isOperatorContent) {
            if (item.access.canCopy) {
                return (
                    <Chip
                        icon={<CopyIcon />}
                        label={translate('library.access.copyAllowed')}
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                );
            } else {
                return (
                    <Chip
                        icon={<LinkIcon />}
                        label={translate('library.access.referenceOnly')}
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                );
            }
        }
        return null;
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': onClick ? { boxShadow: 4 } : {},
            }}
            onClick={onClick ? () => onClick(item) : undefined}
        >
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getTypeIcon()}
                        <Chip
                            label={getTypeLabel()}
                            size="small"
                            color={item.itemType === 'course' ? 'primary' : 'secondary'}
                        />
                    </Box>
                    {item.isOperatorContent && (
                        <Tooltip title={translate('library.operatorContent')}>
                            <OperatorIcon color="action" />
                        </Tooltip>
                    )}
                </Box>

                <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                    {item.title}
                </Typography>

                {item.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {item.description.length > 120
                            ? `${item.description.substring(0, 120)}...`
                            : item.description}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {item.tags?.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" variant="outlined" />
                    ))}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    {getAccessChip()}
                    <Typography variant="caption" color="text.secondary">
                        {translate('library.usageCount', { count: item.usageCount })}
                    </Typography>
                </Box>

                {item.version > 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {translate('library.version', { version: item.version })}
                    </Typography>
                )}
            </CardContent>

            {showActions && (
                <CardActions sx={{ justifyContent: 'space-between' }}>
                    <Box>
                        {onInsert && (
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => onInsert(item)}
                            >
                                {translate('library.actions.insert')}
                            </Button>
                        )}
                    </Box>
                    <Box>
                        {isOperator && item.isOperatorContent && onShare && (
                            <Tooltip title={translate('library.actions.share')}>
                                <IconButton size="small" onClick={() => onShare(item)}>
                                    <ShareIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {onEdit && (
                            <Tooltip title={translate('library.actions.edit')}>
                                <IconButton size="small" onClick={() => onEdit(item)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {onDelete && (
                            <Tooltip title={translate('library.actions.delete')}>
                                <IconButton size="small" onClick={() => onDelete(item)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </CardActions>
            )}
        </Card>
    );
};

export default LibraryItemCard;
